/**
 * used in the announcement bar, this component uses the .vertical-slide classes
 * to have messages slide in and out vertically
 */

import {
  LitElement,
  html,
  css,
  ref,
  createRef,
  unsafeHTML,
  styles,
  paths,
  utils,
  t,
} from '@hh/global.js'
const { prefersReducedMotion } = utils

class HHVerticalCarousel extends LitElement {
  static properties = {
    _current: {
      state: true,
    },
    _queuedPlay: {
      state: false,
    },
    _total: {
      state: true,
    },
    speed: {
      reflect: true,
      type: Number,
      converter: {
        fromAttribute: (value) => {
          return Number(value * 1000)
        },
      },
    },
    playing: {
      reflect: true,
      type: Boolean,
    },
    userPause: {
      type: Boolean,
    },
    autoplay: {
      state: true,
    },
    slideCount: {
      type: Number,
      attribute: 'slide-count',
    }
  }

  $slides = createRef()
  $next = createRef()
  $prev = createRef()

  constructor() {
    super()

    this._total = 0
    this._current = 1
    this.playing = false
    this.userPause = false
  }

  connectedCallback() {
    super.connectedCallback()

    this.items = Array.from(this.querySelectorAll('[slot="item"]'))
    this._total = this.items.length

    this.reducedMotion = prefersReducedMotion();
  }

  disconnectedCallback() {
    super.disconnectedCallback()
  }

  firstUpdated() {
    if (
      this.speed &&
      !this.reducedMotion
    ) {
      this.setPlayPauseIntersectionObserver()
    } else if (this.speed && !this.reducedMotion) {
      this.setAutoplay()
    } else {
      this.autoplay = false
      this.userPause = true
    }
  }

  // Autoplay methods
  setAutoplay() {
    if (!this.speed) return

    clearTimeout(this._queuedPlay);
    this._queuedPlay = setTimeout(() => {
      this.play();
    }, this.speed);

    clearInterval(this.autoplay)
    this.autoplay = setInterval(this.runAnimation.bind(this), this.speed)
  }

  stopAutoplay() {
    clearInterval(this.autoplay)
    this.pause()
  }

  play() {
    this.container?.setAttribute('aria-live', 'off')
    this.playing = true
    this.classList.remove("paused");
  }

  pause() {
    this.container?.setAttribute('aria-live', 'polite')
    this.playing = false
    this.classList.add("paused")
  }

  runAnimation(forward = true, tab = false) {
    if (this._current >= this._total) {

      if (tab && forward) {
        this._current = this._total
      } else if (tab && !forward) {
        this._current--
      } else {
        this._current = 1
      }

    } else if (this._current == 1 && !forward) {
      this._current = 1
    } else {
      forward ? this._current++ : this._current--
    }

    this.items.forEach(el => el.classList.remove('active'))
    this.items[this._current - 1].classList.add('active')  
  }

  setPlayPauseIntersectionObserver() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) =>
        entry.isIntersecting ? this.onMouseLeave() : this.onMouseEnter()
      )
    })
    observer.observe(this.$slides?.value)
  }

  // Move to next or previous slide
  move(e) {
    const byTab = (e == 'tab' || e == 'tab-back')
    if (!byTab) {
      e.preventDefault()
    }

    const forward = (e == 'tab' || e.currentTarget?.name === 'next')
    this.userPause = true
    this.stopAutoplay()
    

    this.runAnimation(forward, byTab)
  }

  // Event handlers
  onMouseEnter() {
    if (this.userPause || this.reducedMotion) return
    this.stopAutoplay()
  }

  onMouseLeave() {
    if (this.userPause || this.reducedMotion) return
    this.setAutoplay()
  }

  keydown(event) {
    if (event.code.toUpperCase() !== 'TAB') return
    event.shiftKey ? this.move('tab-back') : this.move('tab')
  }

  render() {
    return html`<!-- @ts-ignore no-incompatible-type-binding -->
      <div
        class="slides"
        ${ref(this.$slides)}
        @mouseenter="${this.onMouseEnter}"
        @mouseleave="${this.onMouseLeave}"
        @keydown="${this.keydown}"
      >
        <slot name="item"></slot>
      </div>
      ${/* Previous/next buttons */ ''}
      <div class="arrows">
        <button
          ${ref(this.$prev)}
          @click="${this.move}"
          class="arrow arrow--prev"
          name="previous"
          aria-label="${t('accessibility.previous_slide')}"
        >
          ${unsafeHTML(paths.caret)}
        </button>
        <button
          ${ref(this.$next)}
          @click="${this.move}"
          class="arrow arrow--next"
          name="next"
          aria-label="${t('accessibility.next_slide')}"
        >
          ${unsafeHTML(paths.caret)}
        </button>
      </div>`
  }

  static styles = [
    styles.visuallyHidden,
    styles.hidden,
    css`
      *,
      *::before,
      *::after {
        box-sizing: border-box;
      }

      :host {
        position: relative;
      }

      .slides {
        height: inherit;
        overflow-y: hidden;
        position: relative;
        width: 100%;
        display: grid;
      }

      .arrows {
        bottom: 0;
        column-gap: 0.5rem;
        display: flex;
        position: absolute;
        top: 0;
      }

      @media (prefers-reduced-motion: no-preference) {
        .arrows {
          display: none;
        }
      }

      .arrow {
        background-color: transparent;
        border: none;
        cursor: pointer;
        width: 2rem;
      }

      .arrow--prev {
        transform: rotate(90deg);
      }

      .arrow--next {
        transform: rotate(-90deg);
      }
    `,
  ]
}

customElements.define('hh-vertical-carousel', HHVerticalCarousel)
